/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from "react"
import { WidgetInstance, WidgetInstanceOptions } from "friendly-challenge"
import { Locale } from "../../app-core/_common/format/localization"
import { isClient } from "../../app-core/_common/rendering"
import styles from "./FriendlyCaptcha.module.scss"

type CaptchaLanguage = "en" | "es" | "de" | "nl" | "fr" | "it" | "pt"

interface FriendlyCaptchaProps {
    doneCallback: WidgetInstanceOptions["doneCallback"]
    errorCallback: WidgetInstanceOptions["doneCallback"]
    locale: Locale
    validationError: boolean
}

const FriendlyCaptcha: React.FC<React.PropsWithChildren<FriendlyCaptchaProps>> = (props) => {
    if (isClient() && window.__UI_TEST__) return <FriendlyCaptchaFake {...props} />
    else return <FriendlyCaptchaReal {...props} />
}

const FriendlyCaptchaReal: React.FC<React.PropsWithChildren<FriendlyCaptchaProps>> = ({
    doneCallback,
    errorCallback,
    locale,
    validationError,
}) => {
    const container = useRef<HTMLDivElement>(null)
    const widget = useRef<WidgetInstance>()

    const captchaLang = localeToCaptchaLang(locale)

    useEffect(() => {
        if (!container.current) return
        let realContainer = container.current.querySelector("div")

        if (!realContainer) {
            realContainer = document.createElement("div")
            realContainer.classList.add("frc-captcha")
            realContainer.classList.add(styles.container)
            realContainer.setAttribute("data-sitekey", "FCMV2F0P8HQEJLEJ")
            realContainer.setAttribute("data-lang", captchaLang)
            container.current.replaceChildren(realContainer)
        }

        widget.current = new WidgetInstance(realContainer, {
            startMode: "auto",
            doneCallback,
            errorCallback,
        })

        return () => {
            if (widget.current != undefined) {
                widget.current.destroy()
                widget.current = undefined
            }
        }
    }, [captchaLang, doneCallback, errorCallback])

    useEffect(() => {
        if (validationError) {
            widget.current?.reset()
        }
    }, [validationError])

    return <div ref={container}></div>
}

export const defaultCaptchaErrorHandler: FriendlyCaptchaProps["errorCallback"] = (error) => {
    console.error("FriendlyCaptcha error:", error)
}

const FriendlyCaptchaFake: React.FC<React.PropsWithChildren<FriendlyCaptchaProps>> = ({ doneCallback }) => {
    useEffect(() => {
        doneCallback("FAKE")
    }, [doneCallback])

    return <div className="frc-text">I am human</div>
}

const localeToCaptchaLang = (locale: Locale): CaptchaLanguage => {
    switch (locale) {
        case "pt-br":
            return "pt"

        default:
            return locale
    }
}

export default FriendlyCaptcha
